import React, { useState, useEffect, forwardRef, useMemo } from "react";
import { Card, Col } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const CustomCard = forwardRef(
  (
    {
      imgUrl,
      imgUrls,
      title,
      body,
      className = "",
      asCol = true,
      colProps = { md: 12, className: "mb-3" },
      imgRatio = "80%",
      bodyClampLines,
      children,
      cardStyle = {},
    },
    ref
  ) => {
    const [isSmallScreen, setIsSmallScreen] = useState(
      typeof window !== "undefined" ? window.innerWidth <= 768 : false
    );

    // Normalize images to array (prefer imgUrls)
    const images = useMemo(() => {
      if (Array.isArray(imgUrls) && imgUrls.length) return imgUrls.slice();
      if (imgUrl) return [imgUrl];
      return [];
    }, [imgUrls, imgUrl]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [imgErrored, setImgErrored] = useState(false);

    useEffect(() => {
      const handler = () => setIsSmallScreen(window.innerWidth <= 768);
      window.addEventListener("resize", handler);
      return () => window.removeEventListener("resize", handler);
    }, []);

    // reset index / error if images change
    useEffect(() => {
      setCurrentIndex(0);
      setImgErrored(false);
    }, [images.length]);

    // Helper to normalize public paths and some relative inputs
    const normalizeSrc = (src) => {
      if (!src) return src;
      if (typeof src !== "string") return src;
      if (src.startsWith("/")) return src;
      if (/^(https?:)?\/\//.test(src)) return src;
      // convert common relative paths like ../img/foo.jpg or ./img/foo.jpg to /img/foo.jpg
      return src.replace(/^(\.\/|\.\.\/)+/, "/");
    };

    const currentSrc = images.length ? normalizeSrc(images[currentIndex]) : null;

    console.debug("CustomCard image src:", currentSrc, "images:", images);

    const prevImage = () => {
      if (!images.length) return;
      setImgErrored(false);
      setCurrentIndex((p) => (p === 0 ? images.length - 1 : p - 1));
    };

    const nextImage = () => {
      if (!images.length) return;
      setImgErrored(false);
      setCurrentIndex((p) => (p === images.length - 1 ? 0 : p + 1));
    };

    const content = (
      <Card ref={ref} className={`shadow d-flex flex-column ${className}`} style={cardStyle}>
        <div
          className="position-relative w-100 overflow-hidden rounded-top"
          style={{ paddingTop: imgRatio, background: "#f8f9fa" }}
        >
          {currentSrc ? (
            <>
              {!imgErrored ? (
                <img
                  src={currentSrc}
                  alt={typeof title === "string" ? title : "card image"}
                  className="position-absolute top-0 start-0 w-100 h-100"
                  style={{ objectFit: "cover" }}
                  onError={() => setImgErrored(true)}
                  draggable={false}
                />
              ) : (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ background: "#eee", color: "#777" }}
                >
                  <span>Image unavailable</span>
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    aria-label="Previous image"
                    className="position-absolute top-50 start-0 translate-middle-y btn btn-light"
                    style={{
                      opacity: 0.85,
                      marginLeft: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "6px 8px",
                      borderRadius: "50%",
                      zIndex: 5,
                    }}
                  >
                    <FaChevronLeft />
                  </button>

                  <button
                    type="button"
                    onClick={nextImage}
                    aria-label="Next image"
                    className="position-absolute top-50 end-0 translate-middle-y btn btn-light"
                    style={{
                      opacity: 0.85,
                      marginRight: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "6px 8px",
                      borderRadius: "50%",
                      zIndex: 5,
                    }}
                  >
                    <FaChevronRight />
                  </button>

                  <div
                    className="position-absolute start-50 translate-middle-x"
                    style={{ bottom: 8, display: "flex", gap: 6, zIndex: 6 }}
                  >
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setImgErrored(false);
                          setCurrentIndex(idx);
                        }}
                        aria-label={`Go to image ${idx + 1}`}
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          border: "none",
                          padding: 0,
                          background: idx === currentIndex ? "#fff" : "rgba(255,255,255,0.6)",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.06) inset",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ background: "#f2f2f2", color: "#777" }}
            >
              <span>No image</span>
            </div>
          )}
        </div>

        <Card.Body className="d-flex flex-column">
          <Card.Title
            className="text-center"
            style={{
              color: "#58c459",
              fontWeight: 600,
              fontSize: 22,
              marginTop: "0.75rem",
              marginBottom: "0.5rem"
            }}
          >
            {title}
          </Card.Title>

          <Card.Text
            className="mx-3 flex-grow-1"
            style={{
              fontSize: isSmallScreen ? 13 : 16,
              lineHeight: 1.5,
              ...(bodyClampLines
                ? {
                  display: "-webkit-box",
                  WebkitLineClamp: bodyClampLines,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }
                : {}),
            }}
          >
            {body}
          </Card.Text>

          {/* Only add margin when the button (view more) exists */}
          {children && (
            <div className="text-center mt-2 mb-2">
              {children}
            </div>
          )}
        </Card.Body>

      </Card>
    );

    const mergedColProps = { md: 12, className: "mb-3", ...colProps };
    return asCol ? <Col {...mergedColProps}>{content}</Col> : content;
  }
);

export default CustomCard;
